import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { createSelector } from 'reselect';
import { v4 as uuid } from 'uuid';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import { getSoftPullConsentProvided } from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import { shouldFetchAltDealStructures } from '@makemydeal/dr-dash-store/selectors/altDealStructureSelectors';
import {
    markAltDealStructuresFresh,
    applyAlternativeDealStructure
} from '@makemydeal/dr-dash-store/actions/altDealStructureActionCreators';
import { retryAltStructureLeaseRow } from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { buildOfferSavePayloadObject } from '@makemydeal/dr-dash-store/utils/offerPayloadUtil';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import type { ISaveOffer } from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { FINANCE, type AltDealStructureGridCell } from '@makemydeal/dr-platform-types';
import { AltDealStructureDataProvider, useAiAltDealStructureOpenState } from './contexts';
import { selectLeaseSlots } from './LeaseStructuresGrid';
import type { GridSelectionState } from './contexts/ActionsContext';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { useProgressiveFetch } from './hooks/useProgressiveFetch';
import type { FetchBatchResponse, HookSlot } from './hooks/useProgressiveFetch';
import type { Slot, TermCombination } from './PaymentStructureGrid/types';
import { DRAFT_DEAL_LIMIT } from '../../../../constants';

const FETCH_TIMEOUT_MS = 30000;

const filterNumbers = (values: (number | null)[]): number[] => values.filter((v): v is number => v != null);

// Last-resort axes when no redux source resolves (e.g. credit tiers not loaded), so the grid never
// renders an empty/header-less shell.
const FALLBACK_TERMS = [36, 48, 60];
const FALLBACK_DOWN_PAYMENTS = [1000, 3000, 5000];

// Finance axes from the deal's payment-grid state (same source as the main dashboard grid; offer is
// always FINANCE here). Prefer the populated grid, else the deal-centered initial lists (guarded —
// getInitial* can throw/pad nulls), else the selected term, else the baseline.
export const selectFinanceTerms = createSelector(
    [
        (state) => offerReduxSelectors.getPaymentGridTerms(state),
        (state) => {
            try {
                return offerReduxSelectors.getInitialTermsMonthsListForGrid(state);
            } catch {
                return [] as (number | null)[];
            }
        },
        (state) => {
            try {
                return offerReduxSelectors.getSelectedTermMonths(state) ?? null;
            } catch {
                return null;
            }
        }
    ],
    (gridTerms: (number | null)[], initialTerms: (number | null)[], selectedTerm: number | null): number[] => {
        const fromGrid = filterNumbers(gridTerms);
        if (fromGrid.length) return fromGrid;
        const fromInitial = filterNumbers(initialTerms);
        if (fromInitial.length) return fromInitial;
        if (selectedTerm != null) return [selectedTerm];
        return FALLBACK_TERMS;
    }
);

export const selectFinanceDownPayments = createSelector(
    [
        (state) => offerReduxSelectors.getPaymentGridCashDown(state),
        (state) => {
            try {
                return offerReduxSelectors.getInitialDownPaymentListForGrid(state);
            } catch {
                return [] as (number | null)[];
            }
        }
    ],
    (gridCashDown: (number | null)[], initialDownPayments: (number | null)[]): number[] => {
        const fromGrid = filterNumbers(gridCashDown);
        const fromInitial = fromGrid.length ? fromGrid : filterNumbers(initialDownPayments);
        const resolved = fromInitial.length ? fromInitial : FALLBACK_DOWN_PAYMENTS;
        // Cash-down can be fractional (e.g. 4030.80); round to match the dashboard (roundValue).
        return resolved.map((value) => Math.round(value));
    }
);

const buildItems = (terms: number[], downPayments: number[]): TermCombination[] =>
    downPayments.flatMap((dp) =>
        terms.map((t) => ({
            id: `finance-${t}-${dp}`,
            term: t,
            downPayment: dp,
            dealType: 'finance' as const,
            batchKey: `dp-${dp}`
        }))
    );

interface BatchBody {
    terms: { month: number; downPayment: number }[];
    dealType: 'finance' | 'lease';
    correlationId: string;
    dealId: string;
    dealerId: number;
    softPullConsentProvided?: boolean;
    // Sent only when enableUnsavedStateForAI is on, so the BFF computes from the live offer (not saved DXG).
    offer?: ISaveOffer;
}

export const AltDealStructureGridContainer: FC<PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();
    const store = useStore();
    const featureEnabled = useSelector(featureToggleSelectors.isAiAltDealStructureRecommendationsEnabled);
    const { isOpen } = useAiAltDealStructureOpenState();
    const shouldFetch = useSelector(shouldFetchAltDealStructures);
    const unsavedStateEnabled = useSelector(featureToggleSelectors.enableUnsavedStateForAI);
    const bffEndpointConfig = useSelector(configSelectors.getServicesBff);
    const dealId = useSelector(offerReduxSelectors.getDealXgId);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const softPullConsentProvided = useSelector(getSoftPullConsentProvided);

    // Seed local axis state from the deal so the slideout can be tweaked without touching the deal; the
    // sync below re-seeds when the main grid changes.
    const financeTerms = useSelector(selectFinanceTerms);
    const financeDownPayments = useSelector(selectFinanceDownPayments);
    const [terms, setTerms] = useState<number[]>(financeTerms);
    const [downPayments, setDownPayments] = useState<number[]>(financeDownPayments);
    const [hasLocalChanges, setHasLocalChanges] = useState(false);

    // Finance results are component-local (no redux cache), so a remount must refetch even when the
    // "fresh" marker keeps shouldFetch false. Cleared on settle (onComplete) so it's stable mid-load.
    const [needsInitialLoad, setNeedsInitialLoad] = useState(true);

    // Re-seed the slideout axes (discarding local tweaks) and refetch when the main grid changes.
    const mainGridAxesKey = `${financeTerms.join(',')}|${financeDownPayments.join(',')}`;
    const lastSyncedAxesKeyRef = useRef(mainGridAxesKey);
    if (lastSyncedAxesKeyRef.current !== mainGridAxesKey) {
        lastSyncedAxesKeyRef.current = mainGridAxesKey;
        setTerms(financeTerms);
        setDownPayments(financeDownPayments);
        setHasLocalChanges(false);
        setNeedsInitialLoad(true);
    }

    // Down payments the user explicitly typed (keyed `${dealType}-${value}`), so the retry banner can
    // tell an invalid typed value (don't retry) from a transient row failure (retryable).
    const [editedDownPaymentKeys, setEditedDownPaymentKeys] = useState<Set<string>>(() => new Set());
    const markEditedDownPayment = useCallback((dealType: 'finance' | 'lease', value: number) => {
        setEditedDownPaymentKeys((prevKeys) => {
            const downPaymentKey = `${dealType}-${value}`;
            if (prevKeys.has(downPaymentKey)) return prevKeys;
            return new Set(prevKeys).add(downPaymentKey);
        });
    }, []);

    const correlationIdRef = useRef<string>(uuid());
    const lastTermsRef = useRef(terms);
    const lastDownPaymentsRef = useRef(downPayments);
    const hookSlotsRef = useRef<HookSlot<AltDealStructureGridCell>[]>([]);
    if (lastTermsRef.current !== terms || lastDownPaymentsRef.current !== downPayments) {
        correlationIdRef.current = uuid();
        lastTermsRef.current = terms;
        lastDownPaymentsRef.current = downPayments;
    }

    const items = useMemo<TermCombination[]>(() => buildItems(terms, downPayments), [terms, downPayments]);

    const endpoint = useMemo(() => {
        const baseUrl = buildFromConfig(bffEndpointConfig, '');
        return `${baseUrl}/ai/alternative-deal-structures`;
    }, [bffEndpointConfig]);

    const buildBatchBody = useCallback(
        (_batchKey: string, batch: TermCombination[]): BatchBody => {
            const body: BatchBody = {
                terms: batch.map((it) => ({ month: it.term, downPayment: it.downPayment })),
                dealType: FINANCE,
                correlationId: correlationIdRef.current,
                dealId,
                dealerId,
                // Read consent from the live store so a consent-triggered refetch sends the post-consent value.
                softPullConsentProvided: getSoftPullConsentProvided(store.getState() as StateTree)
            };
            // When unsaved-state-for-AI is on, send the live offer so the BFF
            // transforms it and the grid reflects unsaved deal changes (not the saved DXG deal)
            if (unsavedStateEnabled) {
                body.offer = buildOfferSavePayloadObject(store.getState() as StateTree);
            }
            return body;
        },
        [dealId, dealerId, unsavedStateEnabled, store]
    );

    const fetchFn = useCallback(
        async (body: unknown, signal: AbortSignal): Promise<FetchBatchResponse<AltDealStructureGridCell>> => {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify(body),
                signal
            });
            if (!response.ok) {
                throw new Error(`alternative-deal-structures HTTP ${response.status}`);
            }
            return (await response.json()) as FetchBatchResponse<AltDealStructureGridCell>;
        },
        [endpoint]
    );

    const onComplete = useCallback(() => {
        // The load has settled (nothing pending) — the initial load is satisfied for this mount.
        setNeedsInitialLoad(false);
        const current = hookSlotsRef.current;
        if (current.length > 0 && current.every((s) => s.status === 'ready')) {
            dispatch(markAltDealStructuresFresh());
            setHasLocalChanges(false);
        }
    }, [dispatch]);

    const handleSetTerms = useCallback((newTerms: number[]) => {
        setHasLocalChanges(true);
        setTerms(newTerms);
    }, []);

    const handleSetDownPayments = useCallback((newDps: number[]) => {
        setHasLocalChanges(true);
        setDownPayments(newDps);
    }, []);

    const { slots: hookSlots, retry: retryBatch } = useProgressiveFetch<TermCombination, AltDealStructureGridCell>({
        items,
        // Gate the fetch on slideout open (no eager mount fetch). Covers the cold load and axis
        // edits, which add new cell ids the hook fetches. Same-axes stale changes (e.g. vehicle swap) go
        // through the refresh effect below — not `shouldFetch` here, which would mark the grid fresh without
        // refetching. A fresh reopen leaves both flags false → reuse cached cells, no refetch.
        enabled: featureEnabled && isOpen && (hasLocalChanges || needsInitialLoad),
        batchBy: (it) => it.batchKey,
        buildBatchBody,
        fetchFn,
        onComplete,
        timeout: FETCH_TIMEOUT_MS
    });

    hookSlotsRef.current = hookSlots;

    // Force a fresh fetch of every grid row. `retry` bypasses both the `enabled` gate (the `isOpen`
    // requirement) and the hook's id-dedup cache, so it fires immediately regardless of slideout state.
    // Items repeat batchKey per term (e.g. dp-1000 for 36/48/60mo), so dedupe to one retry per row.
    const retryAllBatches = useCallback(() => {
        const batchKeys = new Set(items.map((item) => item.batchKey));
        for (const batchKey of batchKeys) {
            retryBatch(batchKey);
        }
    }, [items, retryBatch]);

    // Soft-pull consent verifies credit data, so force an immediate refetch — eager, like the other AI
    // services (aiMiddleware dispatches their fetch thunks directly). Consent can be granted while the
    // slideout is closed, so the `enabled` gate (which requires `isOpen`) would block a normal fetch;
    // `retryBatch` bypasses it and fires immediately. buildBatchBody reads the now-true consent from the
    // live store.
    const prevSoftPullConsentRef = useRef(softPullConsentProvided);
    useEffect(() => {
        const consentJustGranted = softPullConsentProvided === true && prevSoftPullConsentRef.current !== true;
        prevSoftPullConsentRef.current = softPullConsentProvided;
        if (!consentJustGranted || !featureEnabled) return;
        retryAllBatches();
    }, [softPullConsentProvided, featureEnabled, retryAllBatches]);

    // Refresh the grid on open when a deal change marked it stale. Same-axes changes (vehicle swap,
    // price/trade/accessory edits) keep every cell id, so the hook's id-dedup would serve the prior deal's
    // cached cells; force a full retry instead. A deal change always happens with this slideout closed, so
    // we only need the open edge (isOpen false→true while stale). Skip cold loads (needsInitialLoad) — the
    // `enabled` gate handles those. retryAllBatches is intentionally omitted from deps so the effect doesn't
    // re-fire on `items` changes (axis edits), which would re-fetch all axes and mask per-column errors.
    const wasOpenRef = useRef(isOpen);
    useEffect(() => {
        const justOpened = isOpen && !wasOpenRef.current;
        wasOpenRef.current = isOpen;
        if (!featureEnabled || needsInitialLoad || !isOpen || !shouldFetch || !justOpened) return;
        retryAllBatches();
    }, [featureEnabled, isOpen, shouldFetch, needsInitialLoad]);

    // Mechanical per-batch retry (no analytics — dc-mv-296 fires once per user click in the caller).
    const handleRetry = useCallback(
        (batchKey: string) => {
            // Lease rows live in offer-redux (not the finance BFF hook), so retry re-issues the PQ payment.
            // batchKeys: lease `lease-dp-<dp>`, finance `dp-<dp>`.
            const leaseRowPrefix = 'lease-dp-';
            if (batchKey.startsWith(leaseRowPrefix)) {
                dispatch(retryAltStructureLeaseRow(Number(batchKey.slice(leaseRowPrefix.length))));
                return;
            }
            retryBatch(batchKey);
        },
        [dispatch, retryBatch]
    );

    const financeSlots = useMemo<Slot[]>(
        () =>
            items.map((item, i) => {
                const hs = hookSlots[i];
                return {
                    id: item.id,
                    term: item.term,
                    downPayment: item.downPayment,
                    dealType: item.dealType,
                    batchKey: item.batchKey,
                    status: hs?.status ?? 'pending',
                    data: hs?.data
                };
            }),
        [hookSlots, items]
    );

    // Lease only applies to NEW vehicles with dealer leasing enabled; pre-owned (used/CPO) hides lease
    // regardless of returned rates. When the lease section is hidden, drop its slots from the merged set so
    // they don't drive the shared failure/retry banner, grid meta, or create-scenario analytics (matching
    // the parent, which hides the grid on the same condition).
    const isNewVehicle = useSelector(vehicleSelectors.isVehicleNew);
    const hasLeasing = useSelector(dealerSelectors.hasLeasing);
    const shouldShowLeaseGrid = isNewVehicle && hasLeasing;
    const leaseSlots = useSelector(selectLeaseSlots);
    const slots = useMemo<Slot[]>(
        () => (shouldShowLeaseGrid ? [...financeSlots, ...leaseSlots] : financeSlots),
        [shouldShowLeaseGrid, financeSlots, leaseSlots]
    );

    const [selection, setSelection] = useState<GridSelectionState>({ finance: null, lease: null });

    const initializedScopesOrder = useSelector(scopedStateSelectors.getInitializedScopesOrder);
    const activeScenarioCount = Math.max(0, initializedScopesOrder.length - 1);
    const maxScenarios = DRAFT_DEAL_LIMIT - 1;
    const availableSlots = maxScenarios - activeScenarioCount;
    const isAtMaxScenarios = availableSlots <= 0;

    const resetSelection = useCallback(() => {
        setSelection({ finance: null, lease: null });
    }, []);

    const handleCellSelect = useCallback((cell: AltDealStructureGridCell) => {
        setSelection((currentSelection) => {
            const gridType = cell.dealType;
            if (currentSelection[gridType]?.id === cell.id) {
                return { ...currentSelection, [gridType]: null };
            }
            return { ...currentSelection, [gridType]: cell };
        });
    }, []);

    const isCellDisabled = useCallback(
        (cell: AltDealStructureGridCell) => {
            if (isAtMaxScenarios) return true;

            const selectedCount = [selection.finance, selection.lease].filter(Boolean).length;
            const isThisGridSelected = selection[cell.dealType] !== null;

            if (availableSlots === 1 && selectedCount === 1 && !isThisGridSelected) {
                return true;
            }

            return false;
        },
        [isAtMaxScenarios, availableSlots, selection]
    );

    const canCreateScenarios = useMemo(() => {
        const selectedCount = [selection.finance, selection.lease].filter(Boolean).length;
        if (selectedCount === 0) return false;
        if (selectedCount > availableSlots) return false;
        return true;
    }, [selection, availableSlots]);

    const handleCreateScenarios = useCallback(() => {
        if (selection.finance) {
            dispatch(applyAlternativeDealStructure(selection.finance));
        }
        if (selection.lease) {
            dispatch(applyAlternativeDealStructure(selection.lease));
        }
    }, [selection, dispatch]);

    if (!featureEnabled) return null;

    return (
        <AltDealStructureDataProvider
            slots={slots}
            terms={terms}
            setTerms={handleSetTerms}
            downPayments={downPayments}
            setDownPayments={handleSetDownPayments}
            retry={handleRetry}
            onCellSelect={handleCellSelect}
            selectedCells={selection}
            isCellDisabled={isCellDisabled}
            canCreateScenarios={canCreateScenarios}
            handleCreateScenarios={handleCreateScenarios}
            isAtMaxScenarios={isAtMaxScenarios}
            resetSelection={resetSelection}
            markEditedDownPayment={markEditedDownPayment}
            editedDownPaymentKeys={editedDownPaymentKeys}
        >
            {children}
        </AltDealStructureDataProvider>
    );
};
